import { faHeart, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import {
    faChartLine,
    faExclamationTriangle,
    faLifeRing,
    faPencil,
    faThumbtack,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import { Location } from 'history';
import {
    AUTOMATIC_MESSAGE_VIOLATES_RULES,
    DELETE,
    FORUM_POST_ADMINCLUB_MENU,
    FORUM_POST_EDIT_MENU,
    PIN_POST,
    SEND_HEART_TO_username,
    SHOW_USERS_WHO_LIKED_THIS_POST,
    UNPIN_POST,
} from 'mk/autogenerated/translations/PostMenu.4860cd0fb509319125ca08d99dff42b5'
import { isAnonymousUser, isAuthenticated, isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { adminclub_forum_post_url } from 'mk/urls/functions';
import { DateRange1, KeywordFilterType } from 'mk2/apps/dash/containers/SeoPerformance/FilterForm';
import { dashSeoPerformaceUrl } from 'mk2/apps/dash/urls';
import { postSendHeart } from 'mk2/apps/forum/actions';
import PostMenuTrigger from 'mk2/apps/forum/components/PostMenuTrigger';
import { TESTINGS_TOPIC_ID } from 'mk2/apps/forum/constants';
import { postDetailDeletePost, postDetailPostSticky } from 'mk2/apps/forum/containers/PostDetail/PostDetail.actions';
import { POST_DETAIL_MENU_MORE } from 'mk2/apps/forum/containers/PostDetail/PostDetail.constants';
import { ContestEntity, PostEntity } from 'mk2/apps/forum/schemas';
import { forumContestEditUrl, forumPostEditUrl, forumPostLikersUrl } from 'mk2/apps/forum/urls';
import { isCounsellingPost } from 'mk2/apps/forum/utils';
import { heartsUrl } from 'mk2/apps/hearts/urls';
import MenuItem from 'mk2/components/menu/MenuItem';
import { complaintOpenTrigger } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Menu from 'mk2/containers/Menu/Menu';
import { menuToggle } from 'mk2/containers/Menu/Menu.actions';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import { getRequestPermissions, getRequestUser, AppState } from 'mk2/reducers';
import { UserEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import { connect } from 'react-redux';
import styles from './PostMenu.mscss';

interface OwnProps {
    className?: string;
    post: PostEntity;
    isMyAnonymousPost: boolean;
    contest: ContestEntity;
    location: Location;
}

interface StateProps {
    requestUser: UserEntity;
    permissions: string[];
}

interface DispatchProps {
    onSendHeart();
    onToggleMenuMore();
    onReport(entity: ComplaintTargetType, id: number);
    onDelete(id: number);
    onChangeSticky(id: number, sticky: boolean);
}

type Props = OwnProps & StateProps & DispatchProps;

class PostMenu extends React.PureComponent<Props> {

    public render() {
        const {
            post, isMyAnonymousPost, onToggleMenuMore, className,
            requestUser, permissions, contest,
            location, onSendHeart,
        } = this.props;

        if (!isAuthenticated(requestUser)) {
            return null;
        }

        const isAdmin = permissions.indexOf('forum.can_manage_forum') !== -1;
        const isModerator = isAuthenticatedAs(requestUser, post.topic.moderators);
        const isAuthor = isMyAnonymousPost || isAuthenticatedAs(requestUser, post.author);

        if (!post || (post.deletedAt && !isAdmin) || post.topic.deletedAt) {
            return null;
        }

        const postTime = new Date(post.created);
        const commentTimeWithDelay = new Date((new Date (postTime)).setMinutes(postTime.getMinutes() + 2));
        const currentTime = new Date(Date.now());
        const isEditable = commentTimeWithDelay > currentTime;

        const canEdit = isAdmin || (isAuthor && isEditable && !post.deletedAt);
        const isTesting = post.topic.id === TESTINGS_TOPIC_ID;
        const isCounselling = isCounsellingPost(post);
        const showSeoStats = post && permissions.indexOf('dash.can_manage_seo') >= 0;
        const showReport = post && !contest && !isCounselling && !isMyAnonymousPost && !post.deletedAt;
        const showSendHeart = post.author.id !== requestUser.id && !contest && !isCounselling && !isAnonymousUser(post.author) && !post.deletedAt;
        const showWhoLiked = post.likeable /* Homepage has cached likeable as weak ref so likeable is not loaded yet */ && post.likeable.likesCount > 0 && !post.deletedAt;
        const showChangeSticky = (isAdmin || isModerator) && !post.deletedAt;
        const showEdit = canEdit && !isTesting;
        const showDelete = post.canBeDeleted && (post.comments === 0 || isModerator || isAdmin) && !post.deletedAt;
        const showAdminclub = isAdmin;

        return (showReport || showSendHeart || showWhoLiked || showChangeSticky || showEdit || showDelete || showAdminclub || showSeoStats) ? (
            <Menu
                className={cx(styles.PostMenu, className)}
                menuId={POST_DETAIL_MENU_MORE}
                trigger={<PostMenuTrigger onClick={onToggleMenuMore} />}
                triggerShadow={false}
            >
                {showReport ?
                    <MenuItem
                        label={AUTOMATIC_MESSAGE_VIOLATES_RULES}
                        icon={faExclamationTriangle}
                        onClick={this.onReport}
                    /> : null
                }

                {showSendHeart &&
                    <MenuItem
                        label={interpolate(SEND_HEART_TO_username, { username: `${post.author.username}` })}
                        icon={faHeart}
                        link={isAuthenticated(requestUser) ? url(heartsUrl, {}, {
                            to: post.author.username,
                            next: location.pathname,
                        }) : null}
                        onClick={isAuthenticated(requestUser) ? null : onSendHeart}
                        asAnchor={isAuthenticated(requestUser)}
                    />
                }

                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon={faThumbsUp}
                        link={url(forumPostLikersUrl, { topicSlug: post.topic.slug, postSlug: post.slug })}
                    />
                )}

                {showChangeSticky ? (
                    <MenuItem
                        key="sticky"
                        label={post.sticky ? UNPIN_POST : PIN_POST}
                        icon={faThumbtack}
                        className={cx(isAdmin && styles['PostMenu__item--admin'], isModerator && styles['PostMenu__item--moderator'])}
                        onClick={this.onChangeSticky}
                    />
                ) : null}

                {showEdit ?
                    <MenuItem
                        label={FORUM_POST_EDIT_MENU}
                        icon={faPencil}
                        className={cx(isAdmin && styles['PostMenu__item--admin'], isModerator && styles['PostMenu__item--moderator'], isAuthor && styles['PostMenu__item--author'])}
                        link={contest ? url(forumContestEditUrl, {contestId: contest.id}) : url(forumPostEditUrl, {postId: post.id})}
                    /> : null
                }

                {showDelete ?
                    <MenuItem
                        label={DELETE}
                        icon={faTrash}
                        className={cx(isAdmin && styles['PostMenu__item--admin'], isModerator && styles['PostMenu__item--moderator'], isAuthor && styles['PostMenu__item--author'])}
                        onClick={this.onDelete}
                    /> : null
                }

                {showAdminclub && (
                    <MenuItem
                        label={FORUM_POST_ADMINCLUB_MENU}
                        icon={faLifeRing}
                        className={styles['PostMenu__item--admin']}
                        link={adminclub_forum_post_url(post.id)}
                        asAnchor
                    />
                )}

                {showSeoStats && (
                    <MenuItem
                        label="SEO traffic"
                        icon={faChartLine}
                        className={styles['PostMenu__item--admin']}
                        link={
                            url(dashSeoPerformaceUrl, {}, {
                                range1: DateRange1[DateRange1.Past12Weeks],
                                urls: `pageid:forum-post-${post.id}`,
                                keywords: KeywordFilterType[KeywordFilterType.NonBranded],
                            })}
                        asAnchor
                        linkTarget="_blank"
                    />
                )}
            </Menu>
        ) : null;
    }

    private onReport = () => {
        const { onReport, post } = this.props;
        onReport(ComplaintTargetType.ForumThread, post.id);
    };

    private onDelete = () => {
        const { onDelete, post } = this.props;
        onDelete(post.id);
    };

    private onChangeSticky = () => {
        const { post, onChangeSticky } = this.props;
        onChangeSticky(post.id, !post.sticky);
    };

}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        permissions: getRequestPermissions(state),
        requestUser: getRequestUser(state),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onReport: complaintOpenTrigger,
    onToggleMenuMore: () => menuToggle(POST_DETAIL_MENU_MORE),
    onDelete: postDetailDeletePost,
    onChangeSticky: postDetailPostSticky,
    onSendHeart: postSendHeart,
};

export default connect(mapStateToProps, mapDispatchToProps)(PostMenu);
